<script lang="ts" setup>
import type { SocialLoginProvider } from '~kui/types/auth';

import KUIUnifiedOnboardingShell from '~kui/components/KUI/Unified/Onboarding/KUIUnifiedOnboardingShell.vue';

import type { EntryCta, EntrySource } from '~/graphql/generated/graphql';
import type { AboutYouAnswers, OnboardingConfig, OnboardingState } from '~/types/onboarding';

import { useInitiateSocialLogin } from '~/composables/Auth/useInitiateSocialLogin';
import { useSetPasswordWithToken } from '~/composables/Auth/useSetPasswordWithToken';
import { useAnalyticsService } from '~/composables/core';
import { useOnboardingClusterOptions } from '~/composables/Onboarding/useOnboardingClusterOptions';
import { useOnboardingLogin } from '~/composables/Onboarding/useOnboardingLogin';
import { useOnboardingState } from '~/composables/Onboarding/useOnboardingState';
import { useSignupAgeCheck } from '~/composables/Onboarding/useSignupAgeCheck';
import { EVENTS } from '~/constants/events';
import { UNIFIED_ROUTE_TERMS } from '~/constants/routes/unified';
import { ClusterTermsRequirement, RequestOtpCodeSource } from '~/graphql/generated/graphql';
import { parseSelectedEntityFromQuery } from '~/utils/unified/selectedEntity';
import { readUtmKeyFromQuery } from '~/utils/unified/signupLink';

const props = defineProps<{
  config: OnboardingConfig;
  initialState?: Partial<OnboardingState>;
  initialStepId?: string;
  entrySource?: EntrySource | null;
  entryCta?: EntryCta | null;
}>();

const emit = defineEmits<{
  stepChanged: [payload: { stepId: string; stepIndex: number; totalSteps: number }];
  otpVerified: [payload: { isNewUser: boolean; isMatchedInvite: boolean }];
  flowCompleted: [];
  handedOff: [];
}>();

const {
  state,
  currentStep,
  advance,
  resetTo,
  loading,
  finalStepComplete,
} = useOnboardingState(props.config, {
  initialState: props.initialState,
  initialStepId: props.initialStepId,
});

// Step index/total reflect the user's effective path — steps skipped via skipIf
// (e.g. the terms step for returning users) are excluded so the funnel reflects what they saw.
const visibleSteps = computed(() => props.config.steps.filter(s => !s.skipIf?.(state.value)));

watch(
  () => currentStep.value.id,
  (stepId) => {
    const visible = visibleSteps.value;
    const idx = visible.findIndex(s => s.id === stepId);
    emit('stepChanged', {
      stepId,
      stepIndex: idx >= 0 ? idx : 0,
      totalSteps: visible.length,
    });
  },
  { immediate: true },
);

// flush: 'sync' so the parent's handler (which guards onBeforeUnmount's abandoned event) runs
// before the final step's onComplete awaits a redirect that would otherwise unmount us first.
watch(finalStepComplete, (done) => {
  if (done) {
    emit('flowCompleted');
  }
}, { flush: 'sync' });

const route = useRoute();
// The terms step renders this as a raw `target="_blank"` anchor, which bypasses the router
// base — resolve it here so it carries the cluster path prefix (`/au/terms`). KUI can't do this
// itself: it has no router in Storybook.
const termsPath = useRouter().resolve(UNIFIED_ROUTE_TERMS).href;
const { t } = useI18n();
const { requestLoginCode, user } = useAuth();
const { verifyLogin, registerAndLogin, reset: resetLogin } = useOnboardingLogin();
const { check: checkSignupAge } = useSignupAgeCheck();
const analytics = useAnalyticsService();

const { countryStates, marketingBoxStartsTicked } = useOnboardingClusterOptions();

// The about-you step runs before terms, so the picked state is known by the time the box shows.
const marketingBoxDefault = computed(() => marketingBoxStartsTicked(state.value.stateId));

const stepError = ref<string | null>(null);
const isResent = ref(false);
const resendLoading = ref(false);
const resendNonce = ref(0);

// The emailed link is a fresh page load with none of this flow's state, so the lane has to be
// handed to the server that builds the link: it puts these back on the URL the student returns to.
const entryTrackingInput = computed(() => ({
  ...(props.entrySource ? { entrySource: props.entrySource } : {}),
  ...(props.entryCta ? { entryCta: props.entryCta } : {}),
}));

// finalStepComplete keeps the shell in its non-tappable loading state once the
// last step has fired its onComplete (e.g. a redirect is in flight).
const effectiveLoading = computed(() => loading.value || finalStepComplete.value);

/**
 * The one statement of what a login-code request carries.
 *
 * A resend builds the same link as the first send — the student clicks whichever one arrived last,
 * so a resend that carried less would be an entity, a campaign or a lane silently dropped by asking
 * again, and the account born from that link starts with no goal.
 */
const buildLoginCodeInput = (email: string) => {
  const referralCode = route.query.referralCode;
  const utmKey = readUtmKeyFromQuery(route.query);
  const selectedEntity = parseSelectedEntityFromQuery(route.query);

  return {
    email,
    source: RequestOtpCodeSource.Onboarding,
    ...(typeof referralCode === 'string' ? { referralCode } : {}),
    ...(utmKey ? { utmKey } : {}),
    ...(selectedEntity ? { selectedEntity } : {}),
    ...entryTrackingInput.value,
  };
};

const onEmailSubmit = async (email: string) => {
  loading.value = true;
  stepError.value = null;

  const { error } = await requestLoginCode(buildLoginCodeInput(email));

  if (error) {
    stepError.value = getFirstErrorMessageFromCombinedErrors(t, error);
    loading.value = false;
    return;
  }

  state.value = { ...state.value, email };
  analytics.track(EVENTS.LOGIN_EMAIL_SUBMITTED);
  loading.value = false;
};

const { initiateSocialLogin } = useInitiateSocialLogin();

const onSocialLogin = async (provider: SocialLoginProvider) => {
  loading.value = true;
  stepError.value = null;

  const initiated = await initiateSocialLogin(provider, {
    entrySource: props.entrySource,
    entryCta: props.entryCta,
  });

  // On success loading stays true: the browser is hard-navigating to the OAuth provider.
  if (!initiated) {
    stepError.value = t('general.auth.socialLoginFailed');
    loading.value = false;
  }
};

const onOtpSubmit = async (code: string) => {
  // Verifying consumes the code server-side, so a second submit sent underneath the first is
  // answered "invalid code" and paints that over an advance that in fact succeeded.
  if (loading.value) {
    return;
  }

  loading.value = true;
  stepError.value = null;

  const { isNewRole, isMatchedInvite, error, redirected, handedOff } = await verifyLogin({
    email: state.value.email,
    code,
    onHandOff: () => emit('handedOff'),
  });

  if (redirected) {
    return;
  }

  if (error) {
    analytics.track(EVENTS.LOGIN_OTP_SUBMITTED, { correct: 'no' });
    stepError.value = error;
    loading.value = false;
    return;
  }

  analytics.track(EVENTS.LOGIN_OTP_SUBMITTED, { correct: 'yes' });

  // Nobody is signed in yet: /login has the rest of the sign-in, and loading stays on while it opens.
  if (handedOff) {
    return;
  }

  state.value = { ...state.value, otpCode: code, isNewUserRole: isNewRole };
  emit('otpVerified', { isNewUser: isNewRole || isMatchedInvite, isMatchedInvite });
  loading.value = false;
};

const onOtpResend = async () => {
  resendLoading.value = true;
  stepError.value = null;

  const { error } = await requestLoginCode(buildLoginCodeInput(state.value.email));
  resendLoading.value = false;

  if (error) {
    stepError.value = getFirstErrorMessageFromCombinedErrors(t, error);
    return;
  }

  isResent.value = true;
  resendNonce.value++;
  analytics.track(EVENTS.LOGIN_OTP_RESENT);
};

// The shell advances once loading settles without an error; the too-young and terms steps read
// `tooYoung` in their skipIf, so the refusal routes itself.
const onAboutYouSubmit = async (payload: AboutYouAnswers) => {
  loading.value = true;
  stepError.value = null;

  const { canCreateAccount, canBeMarketed, error } = await checkSignupAge(payload.birthMonthYear ?? '');

  if (error) {
    stepError.value = error;
    loading.value = false;
    return;
  }

  state.value = canCreateAccount
    ? { ...state.value, ...payload, canBeMarketed, tooYoung: false }
    // Nothing from this screen is kept for a student the account floor turns away.
    : { ...state.value, firstName: undefined, lastName: undefined, birthMonthYear: undefined, stateId: undefined, canBeMarketed: false, tooYoung: true };
  loading.value = false;
};

const onTooYoungBackHome = () => navigateTo('/');

const passwordChangeToken = ref<string | null>(null);

const onTermsSubmit = async (payload: { marketingOptIn: boolean | null }) => {
  loading.value = true;
  stepError.value = null;

  const { error, passwordChangeToken: token, redirected } = await registerAndLogin({
    email: state.value.email,
    ...(state.value.firstName ? { firstName: state.value.firstName } : {}),
    ...(state.value.lastName ? { lastName: state.value.lastName } : {}),
    ...(state.value.birthMonthYear ? { birthMonthYear: state.value.birthMonthYear } : {}),
    ...(state.value.stateId ? { stateId: state.value.stateId } : {}),
    marketingOptIn: payload.marketingOptIn,
    onHandOff: () => emit('handedOff'),
  });

  if (redirected) {
    return;
  }

  if (error) {
    stepError.value = error;
    loading.value = false;
    return;
  }

  // A first password is only created once the cluster's terms are accepted.
  const termsPending = user.value?.clusterTermsRequirement
    && user.value.clusterTermsRequirement !== ClusterTermsRequirement.None;
  passwordChangeToken.value = termsPending ? null : token;
  state.value = { ...state.value, agreedToTerms: true, canSetPassword: !!passwordChangeToken.value };
  loading.value = false;
};

// The shell reads a settle of `loading` as success, so the save runs on its own flag and `loading`
// only settles once any refusal is on `stepError`.
const passwordSaving = ref(false);

const trackPasswordSetup = (skipped: boolean) => {
  analytics.track(EVENTS.UNIFIED_AUTH_PASSWORD_SETUP_COMPLETED, {
    skipped,
    entrySource: props.entrySource ?? null,
    entryCta: props.entryCta ?? null,
  });
};

const { setPassword } = useSetPasswordWithToken({
  loading: passwordSaving,
  token: passwordChangeToken,
  tokenExpiredMessage: () => t('unified.auth.passwordSetup.tokenExpired'),
  onSaved: () => trackPasswordSetup(false),
});

const onPasswordSetupSubmit = async (password: string) => {
  loading.value = true;
  stepError.value = null;

  const { error } = await setPassword(password);

  if (error) {
    stepError.value = typeof error === 'string' ? error : getFirstErrorMessageFromCombinedErrors(t, error);
  }
  loading.value = false;
};

const onPasswordSetupSkip = () => {
  passwordChangeToken.value = null;
  trackPasswordSetup(true);
};

const handleAdvance = () => {
  stepError.value = null;
  advance();
};

const handleReset = () => {
  if (loading.value) {
    return;
  }

  stepError.value = null;
  isResent.value = false;
  resetLogin();
  resetTo(props.config.resetStepId ?? props.config.steps[0]!.id);
};
</script>

<template>
  <KUIUnifiedOnboardingShell
    :step="currentStep"
    :state="state"
    :loading="effectiveLoading"
    :step-error="stepError"
    :is-resent="isResent"
    :resend-loading="resendLoading"
    :resend-nonce="resendNonce"
    :terms-path="termsPath"
    :country-states="countryStates"
    :marketing-opt-in-default="marketingBoxDefault"
    @advance="handleAdvance"
    @reset="handleReset"
    @email-submit="onEmailSubmit"
    @otp-submit="onOtpSubmit"
    @otp-resend="onOtpResend"
    @about-you-submit="onAboutYouSubmit"
    @too-young-back-home="onTooYoungBackHome"
    @terms-submit="onTermsSubmit"
    @password-setup-submit="onPasswordSetupSubmit"
    @password-setup-skip="onPasswordSetupSkip"
    @social-login="onSocialLogin"
  />
</template>
